import React, { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { IconsEnum } from '../../../constants/icons';
import { ErrorMessageSeverity } from '../../../constants/form';

export type AccordionErrorMessageType = { id: string; severity: ErrorMessageSeverity };

export type AccordionContextType = {
  collapseAll: boolean;
  expanded: string | null;
  setExpanded: Dispatch<SetStateAction<string | null>>;
  error: AccordionErrorMessageType[];
  setError: Dispatch<SetStateAction<AccordionErrorMessageType[]>>;
  iconCollapsed: IconsEnum;
  iconExpanded: IconsEnum;
  onTabChange?: AccordionProviderType['onTabChange'];
  segmentedTitle?: boolean; // if segmented we'll show the vertical separator next to chevron to the right
  //onTabChange?(e: AccordionEventParams): void;
};

export type AccordionProviderType = {
  activeIndex?: number | null;
  children: ReactNode;
  collapseAll?: boolean;
  defaultExpanded?: string;
  error?: AccordionErrorMessageType[];
  iconCollapsed?: IconsEnum;
  iconExpanded?: IconsEnum;
  onTabChange?: (id: number | null) => void;
  segmentedTitle?: boolean; // if segmented we'll show the vertical separator next to chevron to the right
};

export const AccordionContext = createContext<AccordionContextType>({
  collapseAll: false,
  expanded: null,
  setExpanded: () => {},
  error: [],
  setError: () => {},
  iconCollapsed: IconsEnum.arrowDown,
  iconExpanded: IconsEnum.arrowUp,
  segmentedTitle: undefined
});

export const AccordionProvider = ({
  activeIndex,
  collapseAll: collapseAllDefault,
  children,
  defaultExpanded,
  error: defaultError,
  iconExpanded: defaultIconExpanded,
  iconCollapsed: defaultIconCollapsed,
  onTabChange,
  segmentedTitle
}: AccordionProviderType) => {
  const [expanded, setExpanded] = useState<string | null>(defaultExpanded ?? null);
  const [error, setError] = useState<AccordionErrorMessageType[]>(defaultError ?? []);

  const iconExpanded: IconsEnum = defaultIconExpanded ?? IconsEnum.arrowUp;
  const iconCollapsed: IconsEnum = defaultIconCollapsed ?? IconsEnum.arrowDown;

  const collapseAll = !!collapseAllDefault;

  useEffect(() => {
    setExpanded(defaultExpanded ?? null);
  }, [defaultExpanded]);

  useEffect(() => {
    setError(defaultError ?? []);
  }, [defaultError]);

  useEffect(() => {
    if (typeof activeIndex === 'number') {
      React.Children.forEach(children, child => {
        if (React.isValidElement(child)) {
          const { props } = child; // props are child
          const { id, tabindex } = props.children[0].props; //AccordionTitle should be the first elementat index 0
          if (id && typeof tabindex === 'number' && tabindex === activeIndex) {
            setExpanded(id);
          }
        }
      });
    } else {
      setExpanded(null);
    }
  }, [activeIndex, children]);

  return (
    <AccordionContext.Provider
      value={{
        expanded,
        setExpanded,
        error,
        setError,
        iconExpanded,
        iconCollapsed,
        collapseAll,
        onTabChange,
        segmentedTitle
      }}>
      {children}
    </AccordionContext.Provider>
  );
};
